/* Messages — single conversation room. Orchestrates keyboard-aware
   container, message list, typing indicator + composer. Heavy lifting
   lives in ./messageRoom/*. Kept < 200 lines per the Golden Rule. */
import { useEffect, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { useUser } from "../context/UserAuthContext";
import { useCall, CALL_PHASE } from "../context/CallContext";
import { VOICE_CALLS_ENABLED } from "../lib/featureFlags";
import Composer from "./messageRoom/Composer";
import MessageList from "./messageRoom/MessageList";
import RoomHeader from "./messageRoom/RoomHeader";
import { groupMessagesForRender } from "./messageRoom/_groupMessages";
import { useRoomData } from "./messageRoom/useRoomData";
import { useRoomKeyboard } from "./messageRoom/useRoomKeyboard";

export default function MessageRoom() {
  const { id: convId } = useParams();
  const navigate = useNavigate();
  const { user } = useUser();
  const { phase: callPhase, startCall } = useCall();
  const myId = user?.id;

  const scrollRef = useRef(null);
  const taRef = useRef(null);
  const containerRef = useRef(null);
  const composerRef = useRef(null);

  const scrollToEnd = (smooth = true) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: smooth ? "smooth" : "auto" });
  };

  const {
    conv, messages, loading, sending, draft,
    otherTyping, otherSeenMsgId, otherSeenAt,
    handleSend, handleDraftChange,
  } = useRoomData({ convId, myId, scrollToEnd });

  const { keyboardOpen } = useRoomKeyboard({ containerRef, composerRef, scrollRef });

  // Auto-grow textarea in response to draft edits.
  useEffect(() => {
    const ta = taRef.current;
    if (!ta) return;
    ta.style.height = "auto";
    ta.style.height = Math.min(ta.scrollHeight, 140) + "px";
  }, [draft]);

  const otherProfile = useMemo(() => {
    if (!conv || conv.kind === "group") return null;
    return (conv.member_profiles || []).find((p) => p.id !== myId) || null;
  }, [conv, myId]);

  const title = conv?.kind === "group"
    ? (conv?.title || "محادثة جماعية")
    : (otherProfile?.name || "محادثة");

  const subtitle = useMemo(() => {
    if (otherTyping) return "يكتب الآن…";
    if (otherProfile?.role === "lawyer" && otherProfile?.specialty) return otherProfile.specialty;
    if (otherProfile?.role === "lawyer") return "محامٍ";
    if (otherProfile?.role === "client") return "عميل";
    return "";
  }, [otherTyping, otherProfile]);

  const rendered = useMemo(() => groupMessagesForRender(messages), [messages]);

  const handleStartCall = () => {
    if (callPhase !== CALL_PHASE.IDLE || !otherProfile) return;
    startCall({
      conversationId: convId,
      calleeId: otherProfile.id,
      calleeProfile: otherProfile,
    });
  };

  const onFocusScroll = () => {
    setTimeout(() => scrollToEnd(true), 80);
    setTimeout(() => scrollToEnd(true), 400);
  };

  const dismissKeyboard = () => {
    if (document.activeElement === taRef.current) taRef.current?.blur();
  };

  return (
    <div
      ref={containerRef}
      className="text-[#0A192F] flex flex-col fixed left-0 right-0 overflow-hidden"
      dir="rtl"
      data-testid="message-room"
      style={{ background: "#F4F4F2", top: 0, height: "100%" }}
    >
      <RoomHeader
        title={title}
        subtitle={subtitle}
        otherTyping={otherTyping}
        otherProfile={otherProfile}
        conv={conv}
        canCall={VOICE_CALLS_ENABLED && callPhase === CALL_PHASE.IDLE}
        onBack={() => navigate("/messages")}
        onStartCall={handleStartCall}
      />

      <MessageList
        ref={scrollRef}
        loading={loading}
        messages={messages}
        rendered={rendered}
        title={title}
        myId={myId}
        otherProfile={otherProfile}
        otherTyping={otherTyping}
        otherSeenMsgId={otherSeenMsgId}
        otherSeenAt={otherSeenAt}
        onTapDismiss={dismissKeyboard}
      />

      <Composer
        ref={composerRef}
        taRef={taRef}
        draft={draft}
        sending={sending}
        keyboardOpen={keyboardOpen}
        onSubmit={handleSend}
        onChange={handleDraftChange}
        onFocusScroll={onFocusScroll}
      />
    </div>
  );
}
